import { makeAutoObservable, runInAction } from 'mobx';
import { createContext, useContext } from 'react';
import { PaginationDelegate } from 'store/general/paginationDelegate';
import { getRootStoreInstance } from 'store/rootStore';
import { BiGroup, GroupSortBy, RenewalDataFilter, SortOrder } from 'types/groupService';
import { EnrollmentStage, InstalledEnrollmentType } from 'types/enrollmentService';
import { getOrgAffiliation, setUserOrgAffiliation } from 'api/operations/aaaService';
import { refreshToken } from 'api/refreshToken';
import { getRenewalGroupsPage } from 'api/operations/groupService';
import { getInstalledEnrollmentPeriodsByGroupIds } from 'api/operations/enrollmentService';
import { getEmailsByPeriodIds } from 'api/operations/emailService';
import { FullAgency } from 'types/aaaService';
import { COMMON_URLS } from '@insurancemenu/imenu-components-lib/dist/utils/urls';
import { TriggerRenewalsFilter, TriggerRenewalsPeriod } from './types';
import { RenewalGroupDelegate } from './renewalGroupDelegate';
import { RenewalGroupActionDelegate } from './renewalGroupActionDelegate';
import { BatchActionDelegate } from './batchActionDelegate';
import { GenerateFilesForAutoRenewModalDelegate } from './modals/generateFilesForAutoRenewModalDelegate';

function getDefaultEffectiveDate(): string {
  const now = new Date();
  now.setDate(1);
  return now.toISOString().slice(0, 10);
}

export type TriggerRenewalsPaginationDelegate = PaginationDelegate<GroupSortBy, SortOrder, TriggerRenewalsFilter>;

export class TriggerRenewalsPageStore {
  private _rootStore = getRootStoreInstance();
  private _allSponsorAgencies: FullAgency[] = [];
  private _isLoading = false;
  private _isInitialLoading = true;

  private _renewalGroupDelegates: RenewalGroupDelegate[] = [];

  // A delegate for actions performed per-row
  public renewalGroupActionDelegate = new RenewalGroupActionDelegate({
    updateGroup: (updatedGroup: BiGroup): BiGroup | undefined => {
      return this.updateActiveGroup(updatedGroup);
    },
    updateEnrollmentPeriod: (updatedPeriod: InstalledEnrollmentType): void => {
      this.updateActiveEnrollmentPeriod(updatedPeriod);
    },
    removeEnrollmentPeriod: (periodId: string, groupId: string): void => {
      this.removeEnrollmentPeriod(periodId, groupId);
    },
    getUnavailableEffectiveDates: (groupId: string, periodId: string): string[] => {
      const groupDelegate = this._renewalGroupDelegates.find((delegate) => delegate.group.id === groupId);
      return (
        groupDelegate?.enrollmentDelegates
          .filter((enrollmentDelegate) => enrollmentDelegate.period.id !== periodId)
          .map((enrollmentDelegate) => enrollmentDelegate.period.effectiveDate) ?? []
      );
    },
  });

  public paginationDelegate: TriggerRenewalsPaginationDelegate = new PaginationDelegate<
    GroupSortBy,
    SortOrder,
    TriggerRenewalsFilter
  >({
    defaultSortBy: GroupSortBy.UPDATE_TIME,
    defaultSortOrder: SortOrder.DESC,
    filters: {
      effectiveDate: getDefaultEffectiveDate(),
      agencyId: null,
      name: '',
      stage: EnrollmentStage.INSTALLED,
      exhibitsFilter: RenewalDataFilter.ALL,
      ratesFilter: RenewalDataFilter.ALL,
    },
  });

  public batchActionDelegate = new BatchActionDelegate({
    paginationDelegate: this.paginationDelegate,
    getAllPeriods: (): TriggerRenewalsPeriod[] =>
      this._renewalGroupDelegates.flatMap((delegate) =>
        delegate.enrollmentDelegates
          .filter((enrollmentDelegate) => enrollmentDelegate.isAvailableForBatchActions)
          .map((enrollmentDelegate) => enrollmentDelegate.period),
      ),
    reloadPage: (): Promise<void> => this.getRenewalGroupsPage(),
    setLoading: (loading: boolean): void => this.setLoading(loading),
  });

  public generateFilesForAutoRenewModalDelegate = new GenerateFilesForAutoRenewModalDelegate({
    getEffectiveDate: (): string => this.paginationDelegate.filters.effectiveDate,
  });

  public get isInitialLoading(): boolean {
    return this._isInitialLoading;
  }

  public get isLoading(): boolean {
    return this._isLoading;
  }

  public get allSponsorAgencies(): FullAgency[] {
    return this._allSponsorAgencies;
  }

  public get renewalGroupDelegates(): RenewalGroupDelegate[] {
    return this._renewalGroupDelegates;
  }

  public updateActiveEnrollmentPeriod(updatedPeriod: InstalledEnrollmentType): void {
    const groupDelegate = this._renewalGroupDelegates.find((delegate) => delegate.group.id === updatedPeriod.groupId);
    groupDelegate?.updateEnrollmentPeriod(updatedPeriod);
  }

  public updateActiveGroup(updatedGroup: BiGroup): BiGroup | undefined {
    const groupDelegate = this._renewalGroupDelegates.find((delegate) => delegate.group.id === updatedGroup.id);
    return groupDelegate?.updateGroup(updatedGroup);
  }

  public removeEnrollmentPeriod(periodId: string, groupId: string): void {
    const groupDelegate = this._renewalGroupDelegates.find((delegate) => delegate.group.id === groupId);
    groupDelegate?.removeEnrollment(periodId);

    if (groupDelegate && groupDelegate.enrollmentDelegates.length === 0) {
      this._renewalGroupDelegates = this._renewalGroupDelegates.filter((delegate) => delegate.group.id !== groupId);
    }

    const selectedPeriod = this.batchActionDelegate.selectedPeriods.find((period) => period.id === periodId);
    if (selectedPeriod) {
      this.batchActionDelegate.deselectPeriod(selectedPeriod);
    }
  }

  public setIsInitialLoading(isInitialLoading: boolean): void {
    this._isInitialLoading = isInitialLoading;
  }

  public setLoading(loading: boolean): void {
    this._isLoading = loading;
  }

  public async getAllAgencies(): Promise<void> {
    const allAgencies = await getOrgAffiliation();
    runInAction(() => {
      this._allSponsorAgencies = allAgencies.filter((agency) =>
        agency.sponsors.some((agencySponsor) => agencySponsor.id === this._rootStore.userStore.sponsorId),
      );
    });
  }

  public async getRenewalGroupsPage(): Promise<void> {
    this.setIsInitialLoading(true);

    const {
      page,
      filters: { effectiveDate, agencyId, name, stage, exhibitsFilter, ratesFilter },
    } = this.paginationDelegate;

    const renewalGroups = await getRenewalGroupsPage({
      filter: stage,
      name,
      effectiveDate,
      sponsorId: this._rootStore.userStore.sponsorId,
      page,
      agencyId: agencyId || null,
      exhibitsFilter,
      ratesFilter,
    });

    const enrollments = await getInstalledEnrollmentPeriodsByGroupIds(renewalGroups.result.map((group) => group.id));

    const enrollmentPeriodIds = enrollments.map((enrollment) => enrollment.id);
    const emails = await getEmailsByPeriodIds(enrollmentPeriodIds);

    runInAction(() => {
      this._renewalGroupDelegates = renewalGroups.result.map(
        (group) =>
          new RenewalGroupDelegate(
            group,
            enrollments.filter(
              (enrollment) =>
                enrollment.groupId === group.id &&
                (enrollment.productLineIds.length > 0 ||
                  enrollment.stage === EnrollmentStage.INSTALLED ||
                  enrollment.stage === EnrollmentStage.RENEWAL),
            ),
            emails,
          ),
      );
      this.paginationDelegate.setTotalElements(renewalGroups.totalElements);
      this.paginationDelegate.setTotalPages(renewalGroups.totalPages);
      this.batchActionDelegate.resetSelection();
      this.setIsInitialLoading(false);
    });
  }

  public async reloginToGroupProfile(groupId: string, agencyId: string, agencyName: string): Promise<void> {
    this.setLoading(true);

    const domain = this._rootStore.domainStoreV2.domainHeader;
    await setUserOrgAffiliation({
      sponsor: {
        id: domain.id,
        name: domain.name,
      },
      agency: {
        id: agencyId,
        name: agencyName,
      },
      subAgency: {},
    });

    await runInAction(async () => {
      try {
        await refreshToken();
        // Triggers header refresh when sponsor or agency details are updated
        window.location.href = `/group/${groupId}`;
      } catch (error) {
        window.location.href = `https://${COMMON_URLS.authFe}/login`;
      }

      this.setLoading(false);
    });
  }

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }
}

const TriggerRenewalsPageStoreContext = createContext(new TriggerRenewalsPageStore());

const useTriggerRenewalsPageStore = (): TriggerRenewalsPageStore => {
  return useContext(TriggerRenewalsPageStoreContext);
};

export { TriggerRenewalsPageStoreContext, useTriggerRenewalsPageStore };
